/**
 * Upload Catalogue Page for ZAMMER Marketplace
 *
 * Wizard:
 *   1) Category select (shared across the whole catalogue)
 *   2) Bulk hero-image upload — each image becomes a separate product slot
 *   3) Per-product tabs — fill name / price / description / color variants
 *      + add up to 4 angle shots per product. Shared header (fabric, GST,
 *      HSN, description template) pre-fills every tab.
 *
 * One catalogue submission produces N distinct Products (up to 40), all
 * inheriting the catalogue's category and shared fields, each with its own
 * pricing, images, and color × size variants.
 */

import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import SellerLayout from '../../components/layouts/SellerLayout';
import CategorySelector from '../../components/seller/CategorySelector';
import {
  createCatalogue,
  uploadCatalogueImage,
  getCatalogueById,
  deleteCatalogue
} from '../../services/catalogueService';
import { getSizesForCategory, compareSizes } from '../../data/sizeProfiles';
import { colorOptions, swatchStyle } from '../../data/colorOptions';

// Constants
const MAX_PRODUCTS = 40;
const MAX_ANGLES_PER_PRODUCT = 4;
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_IMAGE_FORMATS = ['image/png', 'image/jpeg', 'image/jpg'];

/**
 * Does this catalogue's category describe a lehenga?
 *
 * Matches the whole 'Lehengas' product group under Women > Ethnic Wear (Bridal,
 * Party, Semi-Stitched, Ready-to-Wear, Designer) and also Kids > Girls Wear >
 * Lehengas & Ethnic > Lehenga Choli, while leaving that group's Salwar Suit,
 * Anarkali, Sharara Set and Ghagra alone — those are not lehenga sets and the
 * blouse/dupatta questions do not apply to them.
 */
const isLehengaCategory = ({ categoryLevel3 = '', categoryLevel4 = '' } = {}) =>
  categoryLevel3.trim().toLowerCase() === 'lehengas' ||
  categoryLevel4.toLowerCase().includes('lehenga');

// The three lehenga-only attributes, mirroring the enums on the Product model.
// Rendered as a pair of buttons each rather than a dropdown: two options is
// faster to pick than to open, and it keeps the choice visible on the form.
const LEHENGA_ATTRIBUTES = [
  { field: 'blouseStitching', label: 'Blouse', options: ['Stitched', 'Unstitched'] },
  { field: 'lehengaStitching', label: 'Lehenga', options: ['Stitched', 'Unstitched'] },
  { field: 'dupattaIncluded', label: 'Dupatta', options: ['Yes', 'No'] }
];

// Fabric type options (mirrors backend/models/Product fabricType enum)
const fabricTypeOptions = [
  { value: '', label: 'Select Fabric Type' },
  { value: 'Cotton', label: 'Cotton' },
  { value: 'Silk', label: 'Silk' },
  { value: 'Polyester', label: 'Polyester' },
  { value: 'Linen', label: 'Linen' },
  { value: 'Wool', label: 'Wool' },
  { value: 'Rayon', label: 'Rayon' },
  { value: 'Chiffon', label: 'Chiffon' },
  { value: 'Georgette', label: 'Georgette' },
  { value: 'Velvet', label: 'Velvet' },
  { value: 'Denim', label: 'Denim' },
  { value: 'Satin', label: 'Satin' },
  { value: 'Crepe', label: 'Crepe' },
  { value: 'Net', label: 'Net' },
  { value: 'Lycra', label: 'Lycra' },
  { value: 'Nylon', label: 'Nylon' },
  { value: 'Jacquard', label: 'Jacquard' },
  { value: 'Khadi', label: 'Khadi' },
  { value: 'Organza', label: 'Organza' },
  { value: 'Cotton Blend', label: 'Cotton Blend' },
  { value: 'Silk Blend', label: 'Silk Blend' },
  { value: 'Poly Cotton', label: 'Poly Cotton' }
];

// Size options + canonical ordering now come from data/sizeProfiles — the size
// set shown depends on the catalogue's chosen category (see `sizeOptions` memo
// below). `compareSizes` keeps display + payload in canonical order regardless
// of the seller's click sequence.

// Color palette is the shared single source of truth in data/colorOptions.js

const makeEmptyBucket = (defaults = {}) => ({
  color: defaults.color || 'Black',
  colorCode: defaults.colorCode || '#000000',
  imageUrls: [],
  // Start empty — the valid size set depends on the catalogue's category, which
  // the seller picks before reaching the variants step. Validation requires ≥1
  // size, so the seller actively chooses from the category-correct grid.
  sizes: {}
});

const makeEmptyProduct = (heroImage, descriptionSeed = '') => ({
  heroImage,
  angleImages: [],
  name: '',
  // 🏷️ Seller-only SKU. Unique per product, so it is edited via setChildField at
  // EVERY index (including 0) and must never cascade from the master product.
  sku: '',
  description: descriptionSeed,
  zammerPrice: '',
  mrp: '',
  buckets: [makeEmptyBucket()],
  activeBucketIndex: 0,
  // UI-only override tracking. Keys: 'name' | 'description' | 'zammerPrice' |
  // 'mrp' | 'buckets'. A truthy entry means the seller manually edited that
  // field on this product, and further cascades from Product 1 will skip it.
  // Stripped before submit. Product 1 (index 0) is the master — its own
  // _overrides are unused but kept for shape uniformity.
  _overrides: {}
});

// Reverse a flat variants[] from the backend into the frontend's color-bucket
// shape. Groups by color, accumulates per-size quantities, and unions images.
const reverseVariantsToBuckets = (variants) => {
  if (!Array.isArray(variants) || variants.length === 0) {
    return [makeEmptyBucket()];
  }
  const byColor = new Map();
  for (const v of variants) {
    const color = v.color || 'Default';
    const key = color.toLowerCase();
    const existing = byColor.get(key);
    if (existing) {
      if (v.size && Number(v.quantity) > 0) {
        existing.sizes[v.size] = (existing.sizes[v.size] || 0) + Number(v.quantity);
      }
      if (Array.isArray(v.images)) {
        for (const img of v.images) {
          if (img && !existing.imageUrls.includes(img)) existing.imageUrls.push(img);
        }
      }
    } else {
      byColor.set(key, {
        color,
        colorCode: v.colorCode || '#000000',
        imageUrls: Array.isArray(v.images) ? [...v.images] : [],
        sizes: v.size && Number(v.quantity) >= 0 ? { [v.size]: Number(v.quantity) } : {}
      });
    }
  }
  return Array.from(byColor.values());
};

const UploadCatalogue = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const heroFileInputRef = useRef(null);
  const heroSwapInputRef = useRef(null);
  const angleFileInputRef = useRef(null);

  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingHeroes, setUploadingHeroes] = useState(false);
  const [uploadingAngle, setUploadingAngle] = useState(false);
  const [swappingHero, setSwappingHero] = useState(false);
  const [hydratingDraft, setHydratingDraft] = useState(false);
  // When set, both Save-Draft and Submit will clean up the original draft after
  // creating the replacement catalogue. Avoids variant-reverse round-tripping.
  const [resumingDraftId, setResumingDraftId] = useState(null);

  // Step 1 — Category
  const [categoryValues, setCategoryValues] = useState({
    categoryLevel1: '',
    categoryLevel2: '',
    categoryLevel3: '',
    categoryLevel4: '',
    categoryPath: ''
  });

  // Size grid shown on the variants step depends on the catalogue's category
  // (alphabetical / numeric waist / kids ages / saree free-size). Siblings not
  // explicitly mapped inherit their group's default. See data/sizeProfiles.
  const sizeOptions = useMemo(() => getSizesForCategory({
    level1: categoryValues.categoryLevel1,
    level2: categoryValues.categoryLevel2,
    level3: categoryValues.categoryLevel3
  }), [categoryValues.categoryLevel1, categoryValues.categoryLevel2, categoryValues.categoryLevel3]);

  // Catalogue-level shared header (applied to every product)
  const [shared, setShared] = useState({
    fabricType: '',
    hsnCode: '',
    descriptionTemplate: '',
    // Lehenga-only; collected on the shared header because the category is
    // itself catalogue-wide (step 1 picks one path for every product).
    blouseStitching: '',
    lehengaStitching: '',
    dupattaIncluded: ''
  });

  const showLehengaFields = useMemo(() => isLehengaCategory(categoryValues), [categoryValues]);

  // Step 2/3 — list of products (one per uploaded hero image)
  const [products, setProducts] = useState([]);
  const [activeProductIndex, setActiveProductIndex] = useState(0);

  // ─── Resume draft (from ?resumeDraftId=...) ────────────────────────
  useEffect(() => {
    const draftId = searchParams.get('resumeDraftId');
    if (!draftId || resumingDraftId === draftId) return;

    let cancelled = false;
    setHydratingDraft(true);

    (async () => {
      try {
        const response = await getCatalogueById(draftId);
        if (cancelled) return;

        if (!response.success || !response.data) {
          toast.error(response.message || 'Could not load draft');
          setSearchParams({}, { replace: true });
          return;
        }

        const cat = response.data;
        if (cat.status !== 'draft') {
          toast.error('Only drafts can be resumed for editing');
          setSearchParams({}, { replace: true });
          return;
        }

        // Hydrate category
        setCategoryValues({
          categoryLevel1: cat.category?.level1 || '',
          categoryLevel2: cat.category?.level2 || '',
          categoryLevel3: cat.category?.level3 || '',
          categoryLevel4: cat.category?.level4 || '',
          categoryPath: cat.category?.path || ''
        });

        // Hydrate shared header (descriptionTemplate is frontend-only seed; leave blank)
        setShared({
          fabricType: cat.products?.[0]?.fabricType || '',
          hsnCode: cat.hsnCode || '',
          descriptionTemplate: '',
          blouseStitching: cat.products?.[0]?.blouseStitching || '',
          lehengaStitching: cat.products?.[0]?.lehengaStitching || '',
          dupattaIncluded: cat.products?.[0]?.dupattaIncluded || ''
        });

        // Hydrate products
        const hydratedProducts = (cat.products || []).map(item => {
          const images = Array.isArray(item.images) ? item.images : [];
          return {
            heroImage: images[0] || '',
            angleImages: images.slice(1),
            name: item.name || '',
            sku: item.sku || '',
            description: item.description || '',
            zammerPrice: item.zammerPrice != null ? String(item.zammerPrice) : '',
            mrp: item.mrp != null ? String(item.mrp) : '',
            buckets: reverseVariantsToBuckets(item.variants),
            activeBucketIndex: 0,
            // Fresh editing session — no overrides carried from server. Every
            // field starts un-overridden so a re-typed Product 1 cascades
            // naturally into children once again.
            _overrides: {}
          };
        });

        setProducts(hydratedProducts);
        setActiveProductIndex(0);
        setResumingDraftId(draftId);
        setCurrentStep(hydratedProducts.length > 0 ? 3 : 2);

        toast.info(`Resumed draft with ${hydratedProducts.length} product(s). Submitting will replace the original.`);
      } catch (err) {
        if (cancelled) return;
        console.error('[RESUME-DRAFT] Failed:', err);
        toast.error(err.message || 'Failed to load draft');
        setSearchParams({}, { replace: true });
      } finally {
        if (!cancelled) setHydratingDraft(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, resumingDraftId, setSearchParams]);

  // ─── Category helpers ──────────────────────────────────────────────
  const setFieldValue = (field, value) => {
    setCategoryValues(prev => ({ ...prev, [field]: value }));
  };

  const canProceedToStep2 = () =>
    categoryValues.categoryLevel1 && categoryValues.categoryLevel2 && categoryValues.categoryLevel3;

  // ─── Shared header helpers ─────────────────────────────────────────
  const updateShared = (field, value) => {
    setShared(prev => ({ ...prev, [field]: value }));
  };

  // Description Template → live copy-paste into every product's `description`.
  // Children whose `_overrides.description` is already true are protected, so
  // a manual edit on Product 2/3/4's Description survives subsequent template
  // typing. Product 1 (idx 0) is the master and always follows the template.
  useEffect(() => {
    const tmpl = shared.descriptionTemplate;
    if (!tmpl || tmpl.trim() === '') return;
    setProducts(prev => prev.map((p, idx) => {
      if (idx > 0 && p._overrides?.description) return p;
      return { ...p, description: tmpl };
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shared.descriptionTemplate]);

  // ─── Image upload helpers (Cloudinary) ─────────────────────────────
  const validateFile = (file) => {
    if (!ALLOWED_IMAGE_FORMATS.includes(file.type)) {
      toast.error(`Invalid format: ${file.name}. Only PNG and JPEG.`);
      return false;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      toast.error(`${file.name} is too large (${sizeMB}MB). Max ${MAX_FILE_SIZE_MB}MB.`);
      return false;
    }
    return true;
  };

  // Step 2: bulk hero upload — each file becomes a fresh product slot.
  const handleHeroUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (products.length + files.length > MAX_PRODUCTS) {
      toast.error(`Maximum ${MAX_PRODUCTS} products per catalogue`);
      return;
    }

    setUploadingHeroes(true);

    try {
      const newProducts = [];
      for (const file of files) {
        if (!validateFile(file)) continue;
        const response = await uploadCatalogueImage(file);
        if (response.success) {
          newProducts.push(makeEmptyProduct(response.data.url, shared.descriptionTemplate));
        } else {
          const msg = response.message || response.error || 'Unknown server error';
          toast.error(`Failed to upload ${file.name}: ${msg}`);
        }
      }

      if (newProducts.length > 0) {
        setProducts(prev => [...prev, ...newProducts]);
        toast.success(`${newProducts.length} product slot(s) added`);
      }
    } catch (error) {
      console.error('[HERO-UPLOAD] Exception:', error);
      toast.error(`Upload error: ${error.message || 'Failed to upload images'}`);
    } finally {
      setUploadingHeroes(false);
      if (heroFileInputRef.current) heroFileInputRef.current.value = '';
    }
  };

  const removeProduct = (index) => {
    setProducts(prev => prev.filter((_, i) => i !== index));
    setActiveProductIndex(prev => Math.max(0, Math.min(prev, products.length - 2)));
  };

  const canProceedToStep3 = () => products.length >= 1;

  // ─── Cascade engine (Product 1 → 2/3/4 with sticky child overrides) ───
  // Scalar field cascade fields. 'buckets' is handled separately as an
  // atomic array — see cascadeBucketsFromMaster / markChildBucketsOverride.
  const cloneBuckets = (bs) => bs.map(b => ({
    ...b,
    imageUrls: [...b.imageUrls],
    sizes: { ...b.sizes }
  }));

  // Master (Product 1) field edit — propagate to every child whose
  // _overrides[field] is still falsy. Children whose field has been
  // manually edited stay untouched.
  const setMasterField = (field, value) => {
    setProducts(prev => prev.map((p, idx) => {
      if (idx === 0) return { ...p, [field]: value };
      if (p._overrides?.[field]) return p;
      return { ...p, [field]: value };
    }));
  };

  // Child field edit — only that child changes, and the field is marked as
  // overridden so future master edits leave it alone.
  const setChildField = (i, field, value) => {
    setProducts(prev => prev.map((p, idx) => {
      if (idx !== i) return p;
      return {
        ...p,
        [field]: value,
        _overrides: { ...(p._overrides || {}), [field]: true }
      };
    }));
  };

  // Unified field setter — picks master vs child path based on i.
  const setProductField = (i, field, value) => {
    if (i === 0) setMasterField(field, value);
    else setChildField(i, field, value);
  };

  // Bucket cascade — buckets are atomic. After a master bucket op, push the
  // new array (deep-cloned) into every child whose buckets isn't overridden.
  const cascadeBucketsFromMaster = () => {
    setProducts(prev => {
      const masterBuckets = prev[0]?.buckets;
      if (!masterBuckets) return prev;
      return prev.map((p, idx) => {
        if (idx === 0) return p;
        if (p._overrides?.buckets) return p;
        const cloned = cloneBuckets(masterBuckets);
        return {
          ...p,
          buckets: cloned,
          activeBucketIndex: Math.min(p.activeBucketIndex, cloned.length - 1)
        };
      });
    });
  };

  // Mark a child product's `buckets` as overridden after the seller mutates
  // any of its buckets directly (add/remove color, toggle size, set qty).
  const markChildBucketsOverride = (i) => {
    if (i === 0) return; // master never overrides itself
    setProducts(prev => prev.map((p, idx) => {
      if (idx !== i) return p;
      if (p._overrides?.buckets) return p;
      return { ...p, _overrides: { ...(p._overrides || {}), buckets: true } };
    }));
  };

  // Hero swap — fresh re-upload from file picker, replaces heroImage.
  // Also drops the stale URL from any of this product's buckets.
  const handleHeroSwap = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!validateFile(file)) {
      if (heroSwapInputRef.current) heroSwapInputRef.current.value = '';
      return;
    }

    setSwappingHero(true);
    try {
      const response = await uploadCatalogueImage(file);
      if (response.success) {
        const newUrl = response.data.url;
        setProducts(prev => prev.map((p, idx) => {
          if (idx !== activeProductIndex) return p;
          const oldHero = p.heroImage;
          return {
            ...p,
            heroImage: newUrl,
            buckets: p.buckets.map(b => ({
              ...b,
              imageUrls: b.imageUrls.filter(u => u !== oldHero)
            }))
          };
        }));
        toast.success('Hero image updated');
      } else {
        toast.error(response.message || 'Failed to swap hero');
      }
    } catch (error) {
      console.error('[HERO-SWAP] Exception:', error);
      toast.error(error.message || 'Failed to swap hero');
    } finally {
      setSwappingHero(false);
      if (heroSwapInputRef.current) heroSwapInputRef.current.value = '';
    }
  };

  const handleAddAngle = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const product = products[activeProductIndex];
    const remaining = MAX_ANGLES_PER_PRODUCT - product.angleImages.length;
    if (remaining <= 0) {
      toast.error(`Max ${MAX_ANGLES_PER_PRODUCT} angle images per product`);
      if (angleFileInputRef.current) angleFileInputRef.current.value = '';
      return;
    }

    const filesToUpload = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.info(`Only first ${remaining} angle(s) will be uploaded (max ${MAX_ANGLES_PER_PRODUCT} per product)`);
    }

    setUploadingAngle(true);
    try {
      const newUrls = [];
      for (const file of filesToUpload) {
        if (!validateFile(file)) continue;
        const response = await uploadCatalogueImage(file);
        if (response.success) {
          newUrls.push(response.data.url);
        } else {
          toast.error(`Failed to upload ${file.name}: ${response.message || 'Unknown error'}`);
        }
      }
      if (newUrls.length > 0) {
        setProducts(prev => prev.map((p, idx) => {
          if (idx !== activeProductIndex) return p;
          return { ...p, angleImages: [...p.angleImages, ...newUrls] };
        }));
        toast.success(`${newUrls.length} angle image(s) added`);
      }
    } catch (error) {
      console.error('[ANGLE-UPLOAD] Exception:', error);
      toast.error(error.message || 'Failed to upload angle images');
    } finally {
      setUploadingAngle(false);
      if (angleFileInputRef.current) angleFileInputRef.current.value = '';
    }
  };

  const removeAngleImage = (i, url) => {
    setProducts(prev => prev.map((p, idx) => {
      if (idx !== i) return p;
      return {
        ...p,
        angleImages: p.angleImages.filter(u => u !== url)
      };
    }));
  };

  // ─── Bucket helpers (per product) ──────────────────────────────────
  const updateBuckets = (i, mutator) => {
    setProducts(prev => prev.map((p, idx) => {
      if (idx !== i) return p;
      return { ...p, buckets: mutator(p.buckets) };
    }));
  };

  const addBucket = (i) => {
    const product = products[i];
    const usedColors = new Set(product.buckets.map(b => b.color));
    const fresh = colorOptions.find(c => !usedColors.has(c.name));
    if (!fresh) {
      toast.error(`All ${colorOptions.length} standard colors are already used for this product`);
      return;
    }
    updateBuckets(i, buckets => [...buckets, makeEmptyBucket({ color: fresh.name, colorCode: fresh.code })]);
    setProducts(prev => prev.map((p, idx) =>
      idx === i ? { ...p, activeBucketIndex: p.buckets.length - 1 } : p
    ));
    if (i === 0) cascadeBucketsFromMaster();
    else markChildBucketsOverride(i);
  };

  const removeBucket = (i, j) => {
    const product = products[i];
    if (product.buckets.length <= 1) {
      toast.error('At least one color is required per product');
      return;
    }
    updateBuckets(i, buckets => buckets.filter((_, idx) => idx !== j));
    setProducts(prev => prev.map((p, idx) => {
      if (idx !== i) return p;
      return { ...p, activeBucketIndex: Math.max(0, Math.min(p.activeBucketIndex, p.buckets.length - 1)) };
    }));
    if (i === 0) cascadeBucketsFromMaster();
    else markChildBucketsOverride(i);
  };

  const updateBucket = (i, j, patch) => {
    updateBuckets(i, buckets => buckets.map((b, idx) => (idx === j ? { ...b, ...patch } : b)));
  };

  const setBucketColor = (i, j, colorName) => {
    const found = colorOptions.find(c => c.name === colorName);
    updateBucket(i, j, {
      color: colorName,
      colorCode: found ? found.code : '#000000'
    });
    if (i === 0) cascadeBucketsFromMaster();
    else markChildBucketsOverride(i);
  };

  const toggleBucketSize = (i, j, size) => {
    updateBuckets(i, buckets => buckets.map((b, idx) => {
      if (idx !== j) return b;
      const next = { ...b.sizes };
      if (next[size] != null) delete next[size];
      else next[size] = 1;
      return { ...b, sizes: next };
    }));
    if (i === 0) cascadeBucketsFromMaster();
    else markChildBucketsOverride(i);
  };

  const setBucketSizeQty = (i, j, size, qty) => {
    const q = Math.max(0, Number(qty) || 0);
    updateBuckets(i, buckets => buckets.map((b, idx) => {
      if (idx !== j) return b;
      return { ...b, sizes: { ...b.sizes, [size]: q } };
    }));
    if (i === 0) cascadeBucketsFromMaster();
    else markChildBucketsOverride(i);
  };

  const setActiveBucket = (i, j) => {
    setProducts(prev => prev.map((p, idx) =>
      idx === i ? { ...p, activeBucketIndex: j } : p
    ));
  };

  // ─── Validation ────────────────────────────────────────────────────
  // Pure helper over the product param — useCallback gives it a stable identity
  // so downstream useMemos can list it as a dependency without re-running every
  // render.
  const getProductIssues = useCallback((p) => {
    const issues = [];
    if (!p.heroImage) issues.push('Upload a hero image');
    if (!p.name || !p.name.trim()) issues.push('Add a product name');
    if (!p.sku || !p.sku.trim()) issues.push('Add a SKU');
    if (!p.zammerPrice || Number(p.zammerPrice) <= 0) issues.push('Add a Selling Price');
    if (!p.mrp || Number(p.mrp) <= 0) issues.push('Add an MRP');
    if (p.zammerPrice && p.mrp && Number(p.mrp) < Number(p.zammerPrice)) {
      issues.push('MRP must be ≥ Selling Price');
    }
    if (!p.buckets || p.buckets.length === 0) {
      issues.push('Add at least one color variant');
    } else {
      p.buckets.forEach(b => {
        const label = b.color || 'Untitled';
        if (!b.color || !b.colorCode) {
          issues.push(`Color "${label}" needs a color picked`);
          return;
        }
        // Per-color image assignment removed — colors inherit the product's
        // hero + angle shots automatically. No imageUrls check.
        const sizeEntries = Object.entries(b.sizes);
        if (sizeEntries.length === 0) {
          issues.push(`Color "${label}" needs at least 1 size selected`);
        } else if (!sizeEntries.some(([, qty]) => Number(qty) > 0)) {
          issues.push(`Color "${label}" needs stock > 0 on at least one size`);
        }
      });
    }
    return issues;
  }, []);

  const productIsValid = useCallback(
    (p) => getProductIssues(p).length === 0,
    [getProductIssues]
  );

  const validProductCount = useMemo(
    () => products.filter(productIsValid).length,
    [products, productIsValid]
  );

  const invalidProducts = useMemo(
    () => products
      .map((p, idx) => ({ index: idx, product: p, issues: getProductIssues(p) }))
      .filter(entry => entry.issues.length > 0),
    [products, getProductIssues]
  );

  const canSubmit = () => products.length > 0 && products.every(productIsValid);

  // ─── Payload builder ───────────────────────────────────────────────
  // Each variant inherits the product's full image set (hero + angles) since
  // per-color image picking was removed — colors share the product's photos.
  const flattenBuckets = (buckets, productImages) => {
    const variants = [];
    for (const bucket of buckets) {
      for (const [size, qty] of Object.entries(bucket.sizes)) {
        if (Number(qty) <= 0) continue;
        variants.push({
          size,
          color: bucket.color,
          colorCode: bucket.colorCode,
          quantity: Number(qty),
          images: [...(productImages || [])]
        });
      }
    }
    // Canonical small→large order, regardless of seller's click order.
    variants.sort((a, b) => compareSizes(a.size, b.size));
    return variants;
  };

  const buildCataloguePayload = (status) => {
    return {
      category: {
        level1: categoryValues.categoryLevel1,
        level2: categoryValues.categoryLevel2,
        level3: categoryValues.categoryLevel3,
        level4: categoryValues.categoryLevel4,
        path: categoryValues.categoryPath
      },
      name: `Catalogue ${new Date().toLocaleDateString()}`,
      products: products.map(p => {
        const productImages = [p.heroImage, ...p.angleImages].filter(Boolean);
        return {
          name: (p.name || '').trim(),
          sku: (p.sku || '').trim(),
          description: p.description || shared.descriptionTemplate || '',
          zammerPrice: p.zammerPrice ? Number(p.zammerPrice) : 0,
          mrp: p.mrp ? Number(p.mrp) : 0,
          images: productImages,
          variants: flattenBuckets(p.buckets, productImages),
          fabricType: shared.fabricType || '',
          // Gated on the category rather than cleared from state, so switching
          // away from lehengas and back keeps whatever the seller already chose,
          // while a non-lehenga catalogue never carries these to the API.
          ...(showLehengaFields ? {
            blouseStitching: shared.blouseStitching || '',
            lehengaStitching: shared.lehengaStitching || '',
            dupattaIncluded: shared.dupattaIncluded || ''
          } : {})
        };
      }),
      gst: '',
      hsnCode: shared.hsnCode,
      status
    };
  };

  // Best-effort cleanup of the original draft after a resume → save/submit flow.
  // Failure is logged but never blocks the success path.
  const cleanupResumedDraft = async () => {
    if (!resumingDraftId) return;
    try {
      await deleteCatalogue(resumingDraftId);
    } catch (err) {
      console.warn('[RESUME-CLEANUP] Failed to delete original draft:', err);
    }
  };

  // ─── Submit handlers ───────────────────────────────────────────────
  const handleSaveDraft = async () => {
    if (products.length === 0) {
      toast.error('Upload at least one hero image before saving a draft');
      return;
    }
    setIsLoading(true);
    try {
      const response = await createCatalogue(buildCataloguePayload('draft'));
      if (response.success) {
        await cleanupResumedDraft();
        toast.success('Catalogue saved as draft');
        navigate('/seller/my-catalogues');
      } else {
        toast.error(response.message || 'Failed to save draft');
      }
    } catch (error) {
      console.error('Save draft error:', error);
      toast.error(error.message || 'Failed to save draft');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit()) {
      toast.error('Please complete every product: name, prices, color buckets, sizes with stock.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await createCatalogue(buildCataloguePayload('submitted'));
      if (response.success) {
        await cleanupResumedDraft();
        const created = response.data?.stats?.createdProducts || 0;
        const failed = response.data?.stats?.failedProducts || 0;
        if (failed > 0) {
          toast.warn(`${created} product(s) created, ${failed} failed. Check the catalogue for details.`);
        } else {
          toast.success(`${created} product(s) created successfully!`);
        }
        navigate('/seller/view-products');
      } else {
        toast.error(response.message || 'Failed to submit catalogue');
      }
    } catch (error) {
      console.error('[SUBMIT] Exception:', error);
      toast.error(error.message || 'Failed to submit catalogue');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDiscard = () => {
    if (window.confirm('Discard this catalogue? All uploaded images will be lost.')) {
      navigate('/seller/dashboard');
    }
  };

  // ─── Render: Step Progress ─────────────────────────────────────────
  const renderStepProgress = () => (
    <div className="mb-8">
      <div className="flex items-center justify-center">
        {[1, 2, 3].map((step, index) => (
          <React.Fragment key={step}>
            <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm ${
              currentStep >= step ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {currentStep > step ? (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              ) : step}
            </div>
            {index < 2 && (
              <div className={`w-24 h-1 mx-2 ${currentStep > step ? 'bg-purple-600' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
      <div className="flex justify-center mt-2">
        <div className="flex justify-between w-80 text-xs text-gray-600">
          <span className={currentStep >= 1 ? 'text-purple-600 font-medium' : ''}>Select Category</span>
          <span className={currentStep >= 2 ? 'text-purple-600 font-medium' : ''}>Upload Hero Images</span>
          <span className={currentStep >= 3 ? 'text-purple-600 font-medium' : ''}>Per-Product Details</span>
        </div>
      </div>
    </div>
  );

  // ─── Render: Step 1 ────────────────────────────────────────────────
  const renderStep1 = () => (
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <h2 className="text-xl font-bold text-gray-800 mb-2">Step 1: Select Category</h2>
      <p className="text-gray-600 mb-6">
        Pick the shared category for every product in this catalogue. All products you add
        in the next steps will live under this category.
      </p>

      <CategorySelector
        values={categoryValues}
        setFieldValue={setFieldValue}
        showAllLevels={true}
      />

      <div className="mt-8 flex justify-end">
        <button
          onClick={() => setCurrentStep(2)}
          disabled={!canProceedToStep2()}
          className={`px-6 py-3 rounded-lg font-medium transition-all ${
            canProceedToStep2()
              ? 'bg-purple-600 text-white hover:bg-purple-700'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Upload Hero Images
        </button>
      </div>
    </div>
  );

  // ─── Render: Step 2 ────────────────────────────────────────────────
  const renderStep2 = () => (
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <h2 className="text-xl font-bold text-gray-800 mb-2">Step 2: Upload Hero Images</h2>
      <p className="text-gray-600 mb-6">
        Each image becomes a separate product. Upload one main shot per design — you'll add
        angle shots and details for each in the next step. (1–{MAX_PRODUCTS} products)
      </p>

      {/* Category Display */}
      <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
        <p className="text-sm text-purple-600 font-medium">Selected Category:</p>
        <p className="text-purple-800 font-semibold">{categoryValues.categoryPath}</p>
      </div>

      {/* Upload Area */}
      <div
        onClick={() => heroFileInputRef.current?.click()}
        className="border-2 border-dashed border-purple-300 rounded-xl p-8 text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50/50 transition-all mb-4"
      >
        <svg className="mx-auto h-12 w-12 text-purple-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
        <p className="text-base font-medium text-gray-700 mb-1">
          {uploadingHeroes ? 'Uploading…' : (products.length === 0 ? 'Click to upload hero images' : 'Add more products')}
        </p>
        <p className="text-xs text-gray-500">
          JPEG or PNG | Max {MAX_FILE_SIZE_MB}MB each | up to {MAX_PRODUCTS} products
        </p>
        <input
          ref={heroFileInputRef}
          type="file"
          multiple
          accept="image/png,image/jpeg,image/jpg"
          className="hidden"
          onChange={handleHeroUpload}
          disabled={uploadingHeroes || products.length >= MAX_PRODUCTS}
        />
      </div>

      {/* Product slots */}
      {products.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-800">
              {products.length} product slot{products.length > 1 ? 's' : ''} ready
            </h3>
            <button
              onClick={() => {
                if (window.confirm('Remove all product slots?')) {
                  setProducts([]);
                  setActiveProductIndex(0);
                }
              }}
              className="text-red-500 hover:text-red-600 text-sm"
            >
              Clear All
            </button>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
            {products.map((p, index) => (
              <div key={`${p.heroImage}-${index}`} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 group">
                <img src={p.heroImage} alt={`Product ${index + 1}`} className="w-full h-full object-cover object-top" />
                <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] font-semibold uppercase tracking-wide py-1 text-center">
                  Product {index + 1}
                </div>
                <button
                  onClick={() => removeProduct(index)}
                  className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remove product"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Guidelines */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-800 mb-2">Tips</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Use the cleanest, most representative shot per design as the hero.</li>
          <li>• You'll add up to {MAX_ANGLES_PER_PRODUCT} angle shots (side / back / lateral / detail) per product in Step 3.</li>
          <li>• Recommended: 800×800 px or higher, square aspect.</li>
        </ul>
      </div>

      {/* Navigation */}
      <div className="mt-8 flex justify-between">
        <button
          onClick={() => setCurrentStep(1)}
          className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Back
        </button>
        <button
          onClick={() => setCurrentStep(3)}
          disabled={!canProceedToStep3()}
          className={`px-6 py-3 rounded-lg font-medium transition-all ${
            canProceedToStep3()
              ? 'bg-purple-600 text-white hover:bg-purple-700'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Per-Product Details
        </button>
      </div>
    </div>
  );

  // ─── Render: Step 3 ────────────────────────────────────────────────
  const renderStep3 = () => {
    if (products.length === 0) {
      return (
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
          <svg className="mx-auto h-10 w-10 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-gray-700 font-medium mb-1">No products yet</p>
          <p className="text-gray-500 text-sm mb-5">All products were removed. Go back to upload more hero images or discard this catalogue.</p>
          <div className="flex items-center justify-center space-x-3">
            <button
              onClick={() => setCurrentStep(2)}
              className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium text-sm"
            >
              ← Back to Step 2
            </button>
            <button
              onClick={handleDiscard}
              className="px-5 py-2.5 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 font-medium text-sm"
            >
              Discard Catalogue
            </button>
          </div>
        </div>
      );
    }

    const activeProduct = products[activeProductIndex];
    const activeBucket = activeProduct.buckets[activeProduct.activeBucketIndex];

    return (
      <div className="space-y-4">
        {/* Shared Header */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">Shared across all products</h3>
            <span className="text-xs text-gray-500">Applies to every tab — edit per tab if needed</span>
          </div>

          {/* Lehenga set details. Only rendered for lehenga categories, because
              "is the blouse stitched" is meaningless on a saree or a kurti and
              an always-on field would just be noise for every other seller. */}
          {showLehengaFields && (
            <div className="mb-4 rounded-lg border border-purple-200 bg-purple-50/60 p-4">
              <p className="text-sm font-semibold text-purple-900 mb-1">Lehenga set details</p>
              <p className="text-xs text-purple-700 mb-3">
                Buyers ask this before anything else — and guessing wrong is the most common reason a lehenga comes back.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {LEHENGA_ATTRIBUTES.map(({ field, label, options }) => (
                  <div key={field}>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">{label}</label>
                    <div className="flex gap-2">
                      {options.map((option) => {
                        const active = shared[field] === option;
                        return (
                          <button
                            key={option}
                            type="button"
                            aria-pressed={active}
                            // Tapping the active option clears it, so a seller
                            // who picked the wrong one can get back to "not set"
                            // instead of being stuck with a value they guessed.
                            onClick={() => updateShared(field, active ? '' : option)}
                            className={`flex-1 px-2 py-2 rounded-lg border text-xs font-semibold transition-colors ${
                              active
                                ? 'border-purple-500 bg-purple-600 text-white'
                                : 'border-gray-300 bg-white text-gray-700 hover:border-purple-300'
                            }`}
                          >
                            {option}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fabric Type</label>
              <select
                value={shared.fabricType}
                onChange={(e) => updateShared('fabricType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                {fabricTypeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">HSN Code</label>
              <input
                type="text"
                value={shared.hsnCode}
                onChange={(e) => updateShared('hsnCode', e.target.value)}
                placeholder="e.g. 6105"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description Template
                <span className="text-xs text-gray-500 font-normal ml-2">
                  Auto-applies to every product's description below. Edit per tab to override.
                </span>
              </label>
              <textarea
                value={shared.descriptionTemplate}
                onChange={(e) => updateShared('descriptionTemplate', e.target.value)}
                placeholder="e.g. Pure cotton kurta with traditional design, suitable for daily and festive wear."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
              />
            </div>
          </div>
        </div>

        {/* Tabs row */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">
              Products ({validProductCount}/{products.length} valid)
            </h3>
            <button
              onClick={() => heroFileInputRef.current?.click()}
              disabled={products.length >= MAX_PRODUCTS}
              className="text-purple-600 hover:text-purple-700 text-sm font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              + Add Product
            </button>
            <input
              ref={heroFileInputRef}
              type="file"
              multiple
              accept="image/png,image/jpeg,image/jpg"
              className="hidden"
              onChange={handleHeroUpload}
            />
          </div>

          <div className="flex space-x-2 overflow-x-auto pb-2">
            {products.map((p, index) => {
              const issueCount = getProductIssues(p).length;
              const valid = issueCount === 0;
              const isActive = index === activeProductIndex;
              return (
                <button
                  key={`tab-${index}-${p.heroImage}`}
                  onClick={() => setActiveProductIndex(index)}
                  className={`flex-shrink-0 flex items-center space-x-2 px-3 py-2 rounded-lg border transition-all ${
                    isActive
                      ? 'bg-purple-600 text-white border-purple-600'
                      : valid
                        ? 'bg-white text-gray-700 border-gray-200 hover:border-purple-300'
                        : 'bg-white text-gray-700 border-amber-300 hover:border-amber-400'
                  }`}
                >
                  <img src={p.heroImage} alt="" className="w-7 h-7 rounded object-cover border border-white/20" />
                  <div className="text-left">
                    <div className="text-sm font-medium leading-tight">
                      Product {index + 1}
                    </div>
                    <div className={`text-[10px] leading-tight ${
                      isActive
                        ? 'text-purple-100'
                        : valid ? 'text-emerald-600' : 'text-amber-600'
                    }`}>
                      {valid ? '✓ ready' : `• ${issueCount} issue${issueCount > 1 ? 's' : ''}`}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Active product editor */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-gray-800">
                Editing: Product {activeProductIndex + 1}
              </h3>
              {activeProduct.name?.trim() && activeProduct.name.trim().length <= 60 && (
                <p className="text-xs text-gray-500 truncate max-w-xs mt-0.5">
                  {activeProduct.name.trim()}
                </p>
              )}
            </div>
            {products.length > 1 && (
              <button
                onClick={() => removeProduct(activeProductIndex)}
                className="text-red-500 hover:text-red-600 text-sm"
              >
                Remove this product
              </button>
            )}
          </div>

          {/* Hero + angles */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="md:col-span-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Hero Image</p>
              <div className="relative aspect-square rounded-lg overflow-hidden border border-purple-300">
                <img src={activeProduct.heroImage} alt="Hero" className="w-full h-full object-cover object-top" />
                <span className="absolute top-1 left-1 bg-purple-600 text-white text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded">
                  Hero
                </span>
              </div>
              <button
                onClick={() => heroSwapInputRef.current?.click()}
                disabled={swappingHero}
                className="mt-2 w-full px-3 py-1.5 text-xs font-medium border border-purple-300 text-purple-600 rounded hover:bg-purple-50 disabled:opacity-50"
              >
                {swappingHero ? 'Uploading…' : 'Change Hero'}
              </button>
              <input
                ref={heroSwapInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                className="hidden"
                onChange={handleHeroSwap}
              />
            </div>

            <div className="md:col-span-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Angle Shots ({activeProduct.angleImages.length}/{MAX_ANGLES_PER_PRODUCT})
                </p>
                <button
                  onClick={() => angleFileInputRef.current?.click()}
                  disabled={uploadingAngle || activeProduct.angleImages.length >= MAX_ANGLES_PER_PRODUCT}
                  className="text-xs text-purple-600 hover:text-purple-700 font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  {uploadingAngle ? 'Uploading…' : '+ Add Angle'}
                </button>
                <input
                  ref={angleFileInputRef}
                  type="file"
                  multiple
                  accept="image/png,image/jpeg,image/jpg"
                  className="hidden"
                  onChange={handleAddAngle}
                />
              </div>

              <div className="grid grid-cols-4 gap-2">
                {Array.from({ length: MAX_ANGLES_PER_PRODUCT }).map((_, slot) => {
                  const url = activeProduct.angleImages[slot];
                  if (url) {
                    return (
                      <div key={slot} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 group">
                        <img src={url} alt={`Angle ${slot + 1}`} className="w-full h-full object-cover object-top" />
                        <button
                          onClick={() => removeAngleImage(activeProductIndex, url)}
                          className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          ×
                        </button>
                      </div>
                    );
                  }
                  return (
                    <button
                      key={slot}
                      onClick={() => angleFileInputRef.current?.click()}
                      disabled={uploadingAngle}
                      className="aspect-square rounded-lg border-2 border-dashed border-gray-300 text-gray-400 text-[10px] flex flex-col items-center justify-center hover:border-purple-400 hover:text-purple-500 disabled:opacity-50"
                    >
                      <span className="text-lg leading-none mb-0.5">+</span>
                      <span>Angle {slot + 1}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Per-product fields */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
              <input
                type="text"
                value={activeProduct.name}
                onChange={(e) => setProductField(activeProductIndex, 'name', e.target.value)}
                placeholder="e.g. Royal Blue Cotton Kurta"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* SKU — unique per product, so it uses setChildField (never cascades
                from Product 1). Seller-only; buyers never see it. */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">SKU *</label>
              <input
                type="text"
                value={activeProduct.sku}
                onChange={(e) => setChildField(activeProductIndex, 'sku', e.target.value)}
                placeholder="e.g. GRTN8135MSTD"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono"
              />
              <p className="text-xs text-gray-400 mt-1">
                Unique code for this design — not copied to other products, and never shown to buyers.
              </p>
            </div>

            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={activeProduct.description}
                onChange={(e) => setProductField(activeProductIndex, 'description', e.target.value)}
                placeholder="Describe this specific design — fit, material, occasion…"
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Selling Price (₹) *</label>
              <input
                type="number"
                value={activeProduct.zammerPrice}
                onChange={(e) => setProductField(activeProductIndex, 'zammerPrice', e.target.value)}
                placeholder="0"
                min="0"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">MRP (₹) *</label>
              <input
                type="number"
                value={activeProduct.mrp}
                onChange={(e) => setProductField(activeProductIndex, 'mrp', e.target.value)}
                placeholder="0"
                min="0"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {activeProduct.zammerPrice && activeProduct.mrp &&
             Number(activeProduct.mrp) > Number(activeProduct.zammerPrice) && (
              <div className="lg:col-span-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <span className="text-green-700 font-medium">
                  {Math.round(((Number(activeProduct.mrp) - Number(activeProduct.zammerPrice)) / Number(activeProduct.mrp)) * 100)}% Discount
                </span>
                <span className="text-green-600 ml-2">
                  (Customer saves ₹{(Number(activeProduct.mrp) - Number(activeProduct.zammerPrice)).toFixed(0)})
                </span>
              </div>
            )}
          </div>

          {/* Color buckets */}
          <div className="border-t pt-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-gray-800">Color Variants</h4>
              <button
                onClick={() => addBucket(activeProductIndex)}
                className="text-purple-600 hover:text-purple-700 text-sm font-medium"
              >
                + Add Color
              </button>
            </div>

            <div className="flex space-x-2 mb-4 overflow-x-auto pb-2">
              {activeProduct.buckets.map((bucket, j) => (
                <button
                  key={j}
                  onClick={() => setActiveBucket(activeProductIndex, j)}
                  className={`flex-shrink-0 flex items-center space-x-2 px-3 py-2 rounded-lg border transition-all ${
                    activeProduct.activeBucketIndex === j
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <span className="w-4 h-4 rounded-full border border-gray-300" style={swatchStyle(bucket.colorCode)} />
                  <span className="text-sm font-medium">{bucket.color}</span>
                  <span className={`text-[10px] ${activeProduct.activeBucketIndex === j ? 'text-purple-100' : 'text-gray-400'}`}>
                    ({Object.values(bucket.sizes).filter(q => Number(q) > 0).length} sz)
                  </span>
                </button>
              ))}
            </div>

            {activeBucket && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h5 className="font-medium text-gray-800">Color &amp; Sizes</h5>
                  {activeProduct.buckets.length > 1 && (
                    <button
                      onClick={() => removeBucket(activeProductIndex, activeProduct.activeBucketIndex)}
                      className="text-red-500 hover:text-red-600 text-xs"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                    <div className="flex items-center space-x-2">
                      <select
                        value={activeBucket.color}
                        onChange={(e) => setBucketColor(activeProductIndex, activeProduct.activeBucketIndex, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        {colorOptions.map(c => (
                          <option key={c.name} value={c.name}>{c.name}</option>
                        ))}
                      </select>
                      <div className="w-9 h-9 rounded border" style={swatchStyle(activeBucket.colorCode)} />
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sizes &amp; Stock
                      <span className="text-xs text-gray-500 font-normal ml-2">
                        Click any size — saved in canonical order (XS → 5XL) regardless of click sequence.
                      </span>
                    </label>
                    <div className="grid grid-cols-5 sm:grid-cols-9 gap-2 mb-3">
                      {sizeOptions.map(size => {
                        const enabled = activeBucket.sizes[size] != null;
                        return (
                          <button
                            key={size}
                            onClick={() => toggleBucketSize(activeProductIndex, activeProduct.activeBucketIndex, size)}
                            className={`px-2 py-1.5 rounded border text-sm font-medium transition-colors ${
                              enabled
                                ? 'bg-purple-600 text-white border-purple-600'
                                : 'bg-white text-gray-600 border-gray-300 hover:border-purple-400'
                            }`}
                          >
                            {size}
                          </button>
                        );
                      })}
                    </div>

                    <div className="space-y-2">
                      {Object.entries(activeBucket.sizes)
                        .sort(([a], [b]) => compareSizes(a, b))
                        .map(([size, qty]) => (
                        <div key={size} className="flex items-center space-x-2 text-sm">
                          <span className="w-12 font-medium text-gray-700">{size}</span>
                          <input
                            type="number"
                            value={qty}
                            min="0"
                            onChange={(e) => setBucketSizeQty(activeProductIndex, activeProduct.activeBucketIndex, size, e.target.value)}
                            className="w-24 px-3 py-1.5 border border-gray-300 rounded text-center"
                          />
                          <span className="text-gray-500 text-xs">units in stock</span>
                        </div>
                      ))}
                      {Object.keys(activeBucket.sizes).length === 0 && (
                        <p className="text-xs text-amber-600">Pick at least one size for this color.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Validation Summary */}
        {invalidProducts.length === 0 ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0 font-bold">✓</div>
            <div>
              <p className="text-emerald-800 font-semibold">
                Ready to submit {products.length} product{products.length > 1 ? 's' : ''}
              </p>
              <p className="text-emerald-700 text-sm">
                All tabs are complete. You can submit or save as draft.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <div className="flex items-start space-x-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center flex-shrink-0 font-bold">!</div>
              <div className="flex-1">
                <p className="text-amber-900 font-semibold">
                  {invalidProducts.length} product{invalidProducts.length > 1 ? 's' : ''} need{invalidProducts.length === 1 ? 's' : ''} attention before submit
                </p>
                <p className="text-amber-800 text-sm">
                  Click a row below to jump to that product and fix the issues.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              {invalidProducts.map(({ index, product, issues }) => {
                const isActive = index === activeProductIndex;
                return (
                  <button
                    key={`invalid-${index}`}
                    onClick={() => setActiveProductIndex(index)}
                    className={`w-full text-left bg-white rounded-lg border transition-colors p-3 ${
                      isActive
                        ? 'border-amber-400 ring-2 ring-amber-100'
                        : 'border-amber-200 hover:border-amber-400'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <img
                          src={product.heroImage}
                          alt=""
                          className="w-8 h-8 rounded object-cover object-top border border-gray-200"
                        />
                        <span className="font-medium text-gray-800 text-sm">
                          Product {index + 1}
                        </span>
                      </div>
                      <span className="text-xs bg-amber-100 text-amber-800 font-semibold px-2 py-0.5 rounded-full">
                        {issues.length} issue{issues.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    <ul className="space-y-0.5 ml-10">
                      {issues.map((issue, idx) => (
                        <li key={idx} className="text-xs text-amber-700 flex items-start">
                          <span className="text-amber-400 mr-1.5 mt-0.5">•</span>
                          <span>{issue}</span>
                        </li>
                      ))}
                    </ul>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex justify-between items-center">
            <div className="flex space-x-3">
              <button
                onClick={handleDiscard}
                className="px-4 py-2 text-red-600 hover:text-red-700 font-medium"
              >
                Discard Catalogue
              </button>
              <button
                onClick={() => setCurrentStep(2)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Back
              </button>
            </div>

            <div className="flex items-center space-x-3">
              <span className="text-xs text-gray-500">
                {validProductCount}/{products.length} product(s) ready
              </span>
              <button
                onClick={handleSaveDraft}
                disabled={isLoading}
                className="px-6 py-3 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 font-medium"
              >
                {isLoading ? 'Saving…' : 'Save as Draft'}
              </button>
              <button
                onClick={handleSubmit}
                disabled={isLoading || !canSubmit()}
                title={!canSubmit()
                  ? `${invalidProducts.length} product(s) need attention — see the panel above`
                  : ''}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  canSubmit() && !isLoading
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isLoading ? 'Submitting…' : `Submit ${products.length} Product${products.length > 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <SellerLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800">
            {resumingDraftId ? 'Resume Draft Catalogue' : 'Upload Catalogue'}
          </h1>
          <p className="text-gray-600 mt-1">
            Bulk-upload multiple distinct designs under one category. Each hero image becomes
            a separate product with its own price, description, angle shots, and color variants.
          </p>
        </div>

        {resumingDraftId && (
          <div className="mb-6 bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-start space-x-3">
              <svg className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <div>
                <p className="text-purple-900 font-semibold text-sm">Resumed from saved draft</p>
                <p className="text-purple-700 text-xs">
                  Submitting or re-saving will replace the original draft. Your edits are not yet persisted.
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/seller/my-catalogues')}
              className="text-purple-700 hover:text-purple-900 text-xs font-medium underline"
            >
              ← Back to My Catalogues
            </button>
          </div>
        )}

        {hydratingDraft ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <div className="w-10 h-10 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-700 font-medium">Loading your draft…</p>
          </div>
        ) : (
          <>
            {renderStepProgress()}
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
          </>
        )}
      </div>
    </SellerLayout>
  );
};

export default UploadCatalogue;
